import { useUsers } from '@modules/users/api/users.api';
import type { TProject } from '../../types';
import { useExcludedAssigneeIds } from '../useExcludedAssigneeIds.hook';
import { useContext, useState } from 'react';
import { Avatar as FluentAvatar, Button } from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import { SignInContext } from '@modules/users';
import Avatar from '@modules/users/components/Avatar';
import SearchInput from '@shared/components/SearchInput';
import { StatusDropdown } from '@shared/components/StatusDropdown';
import { TASK_SEARCH_MAX_LENGTH, TASK_STATUSES, TASK_UNASSIGNED_VALUE } from '../constants';
import type { ETaskStatus } from '../types';

type TProps = {
  project: TProject;
  assignee: string[];
  onAssigneeChange: (value: string[]) => void;
  search: string;
  statuses: ETaskStatus[];
  onSearchChange: (search: string) => void;
  onStatusesChange: (statuses: string[]) => void;
  onClear: () => void;
};
const TasksFilters = ({
  project,
  search,
  statuses,
  assignee,
  onAssigneeChange,
  onSearchChange,
  onStatusesChange,
  onClear,
}: TProps) => {
  const { user } = useContext(SignInContext);
  const { users, userMap } = useUsers();
  const excludedUserIds = useExcludedAssigneeIds(project, userMap);
  // Remounting the search box on clear drops typing it has not sent yet, which would otherwise apply after the clear.
  const [searchKey, setSearchKey] = useState(0);
  // Assignable Project participants, the current user first and the rest by name, then tasks without an assignee.
  const assigneeOptions = [
    ...(users ?? [])
      .filter((candidate) => candidate.inviteAccepted && !excludedUserIds.includes(candidate._id))
      .sort(
        (a, b) => Number(b._id === user._id) - Number(a._id === user._id) || (a.name ?? '').localeCompare(b.name ?? '')
      )
      .map((candidate) => ({
        value: candidate._id,
        label: candidate.name || 'Unknown',
        icon: <Avatar user={candidate} size={20} />,
      })),
    { value: TASK_UNASSIGNED_VALUE, label: 'Unassigned', icon: <FluentAvatar color="neutral" size={20} /> },
  ];
  const hasFilters = !!search || statuses.length > 0 || assignee.length > 0;
  return (
    <div className="flex items-stretch gap-3 flex-wrap">
      <SearchInput
        key={searchKey}
        placeholder="Title..."
        value={search}
        maxLength={TASK_SEARCH_MAX_LENGTH}
        onChange={onSearchChange}
        className="flex-1 min-w-56"
      />
      <StatusDropdown
        multiselect
        options={TASK_STATUSES.map((option) => ({
          value: option.value,
          label: option.label,
          icon: <option.icon />,
        }))}
        selectedOptions={statuses}
        onSelect={onStatusesChange}
        placeholder="All statuses"
        className="w-64 max-w-full shrink-0 2xl:w-96"
      />
      <StatusDropdown
        multiselect
        options={assigneeOptions}
        selectedOptions={assignee}
        onSelect={onAssigneeChange}
        placeholder="All assignees"
        className="w-64 max-w-full shrink-0 2xl:w-96"
      />
      <Button
        appearance="subtle"
        icon={<DismissRegular />}
        disabled={!hasFilters}
        onClick={() => {
          setSearchKey((key) => key + 1);
          onClear();
        }}
      >
        Clear filters
      </Button>
    </div>
  );
};
export default TasksFilters;
